import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import Select, {components} from "react-select";
import UAvatar from "../UAvatar";
import {getName, getThumbor} from "../common/function";
import {LIMIT} from "../common/constant";
import {getKeyName, getLabelValueUserImage} from "../common/function";
import useDebouncedEffect from "use-debounced-effect";

const UAsyncEmployeeSelect = React.memo((props) => {
    const [search, setSearch] = useState('')
    const [user, setUser] = useState({})
    const [options, setOptions] = useState(props?.options?.length > 0 ? [...props?.options] : []);

    const handle = {
        change: (e) => {
            // If value is cleared (null/undefined), check for unAssigned option
            if (!e) {
                const unAssignedOption = props?.options?.find(opt => opt.value === 'unAssigned');
                if (unAssignedOption) {
                    props?.onChange(unAssignedOption);
                    setUser(unAssignedOption);
                } else {
                    props?.onChange(e);
                    setUser(e || {});
                }
            } else {
                props?.onChange(e);
                setUser(e);
            }
        }
    }

    useEffect(() => {
        // Update user when props.value changes
        if (props?.value) {
            setUser(props.value);
        } else {
            // When value is cleared, check if there's an unAssigned option
            const unAssignedOption = props?.options?.find(opt => opt.value === 'unAssigned');
            if (unAssignedOption) {
                setUser(unAssignedOption);
            } else {
                setUser({});
            }
        }
    }, [props?.value]);

    useEffect(() => {
        // Update options when props.options changes
        if (props?.options?.length > 0) {
            setOptions([...props?.options]);
        } else {
            setOptions([]);
        }
    }, [props?.options]);

    useEffect(() => {
        return () => off();
    }, []);

    const {off, refresh} = props?.useSocket(res => {
        if (!res.error && res?.data?.socketId === props?.socketId) {
            switch (res.event) {
                case 'hrms|dropList|level|read':
                    let option = [];
                    res?.data?.rows?.forEach(e => {
                        let item = {
                            label: e?.type === 1 ? getName(e) : e?.name,
                            value: e?._id,
                            type: e?.type,
                            profileImage: e?.profileImage,
                            _id: e?._id,
                            employeeId: e?.employeeId
                        }
                        option?.push(item);
                    });
                    setOptions(option);
                    off()
                    break
                case 'hrms|dropList|get':
                    if (res?.data?.listType === 4) {
                        if(props?.excludeClientName) {
                            res.data.rows = res?.data?.rows?.filter(item => !props?.ids?.includes(item?._id));
                        }
                        setOptions(getLabelValueUserImage(res?.data?.rows));
                    }
                    off()
                    break
                default:
                    return true;
            }
        }
    });

    useDebouncedEffect(() => {
        if (search?.length >= LIMIT.SEARCH_CHARACTER) {
            if (props?.level && String(search)?.trim()?.length) props?.sendData('hrms|dropList|level|read', {search, ...(props?.onlySearchUac && {isUac: props?.onlySearchUac}), ...props?.requestData})
            else if(String(search)?.trim()?.length) props?.sendData('hrms|dropList|get', {listType: 4, search, checkVisibility: true, ...props?.requestData})
            refresh();
        } else {
            if(props?.fallbackToDefaultOptions) setOptions(props?.options?.length ? props?.options : [])
            else setOptions([])
        }
    }, LIMIT.DE_BOUNCING_TIME_OUT, [search]);

    const getProps = () => {
        if (props?.params?.isMulti) return {
            MultiValue: (localProps) => (
                <components.MultiValue  {...localProps} >
                    <div className="u_flex u_align-center">
                        <UAvatar name={localProps.data.label} width={24} image={localProps.data.profileImage} s3Url={props?.s3Url} thumbor={getThumbor('32x32')}/>
                        <div>
                        <p className="u_one-line">{localProps.data.label}</p>
                        {localProps.data.employeeId && <p className="u_one-line u_font-12 u_color-light">{localProps.data.employeeId}</p>}
                        </div>
                    </div>
                </components.MultiValue>
            ),
        }
        else return {
            SingleValue: (localProps) => (<components.SingleValue  {...localProps} >
                <div className="u_flex u_align-center">
                    <UAvatar name={localProps?.data?.label} width={24} thumbor={getThumbor('32x32')} image={localProps?.data?.profileImage} s3Url={props?.s3Url}/>
                    <div>
                    <p className="u_one-line">{localProps?.data?.label}</p>
                    {localProps.data.employeeId && <p className="u_one-line u_font-12 u_color-light">{localProps.data.employeeId}</p>}
                    </div>
                </div>
            </components.SingleValue>)
        }
    }

    return (
        <React.Fragment>
            <Select
                name="employee"
                data-testid="fw.uasyncemployeeselect.employee.1.select"
                isClearable={props?.value?.value !== 'unAssigned' && props?.params?.isClearable !== false}
                className={`u_input-select-inner ${props?.params?.isMulti ? 'u_multi-select-user' : 'u_single-select-user'} ${props?.className?.length ? props?.className : ''}`}
                classNamePrefix="u_select"
                placeholder={props?.placeholder}
                value={typeof props?.value === "string" ? (props?.options?.length ? (getKeyName(props?.options, props?.value) || user) : null) : props?.value}
                onInputChange={setSearch}
                onChange={handle.change}
                options={options}
                filterOption={option => option}
                maxMenuHeight={120}
                noOptionsMessage={() => search?.length < 3 ? `Type three character to search ${props?.islineManager ? 'lineManager' : 'employee' }` : 'No Record Found'}
                defaultOptions={options}
                menuPosition={props?.menuPosition}
                components={{
                    Option: (localProps) => (
                        <components.Option {...localProps} >
                            <div className="u_filter-employee-info">
                                <UAvatar name={localProps?.data?.label} width={24} thumbor={getThumbor('32x32')} image={localProps?.data?.profileImage} s3Url={props?.s3Url}/>
                              <div>
                                <p className="u_one-line">{localProps?.data?.label}</p>
                                {localProps.data.employeeId && <p className="u_one-line u_font-12 u_color-light">{localProps.data.employeeId}</p>}
                             </div>
                            </div>
                        </components.Option>
                    ),
                    ClearIndicator: props?.value?.value === 'unAssigned' ? () => null : components.ClearIndicator,
                    ...getProps()
                }}
                {...props?.params}
            />
        </React.Fragment>
    );
});

UAsyncEmployeeSelect.propTypes = {
    value: PropTypes.object,
    options: PropTypes.arrayOf(PropTypes.object),
    params: PropTypes.object,
    className: PropTypes.string,
    onChange: PropTypes.func,
    level: PropTypes.bool,
    onlySearchUac: PropTypes.bool,
    requestData: PropTypes.object,
    disableIds: PropTypes.array,
    sendData: PropTypes.func,
    useSocket: PropTypes.any,
    socketId: PropTypes.string,
    ids: PropTypes.arrayOf(PropTypes.string),
    s3Url: PropTypes.string
};

UAsyncEmployeeSelect.defaultProps = {
    value: null,
    params: {},
    options: [],
    className: '',
    level: false,
    onlySearchUac: false, // only search uac list not employee list
    requestData: {},
    onChange: () => {
    },
    disableIds:[]
}

export default UAsyncEmployeeSelect;
